import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import StatusBadge from '../components/StatusBadge'
import EmbedBadge from '../components/EmbedBadge'
import VideoEmbed from '../components/VideoEmbed'
import './Certificate.css'

function DiagonalWatermark({ text }) {
  const spacing = 200
  return (
    <div className="cert-watermark-layer" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <defs>
          <pattern id="wm-pattern" x="0" y="0" width={spacing} height={spacing} patternUnits="userSpaceOnUse" patternTransform="rotate(-35)">
            <text x={spacing / 2} y={spacing / 2} textAnchor="middle" dominantBaseline="middle"
              fontFamily="monospace" fontSize="11" fontWeight="700" letterSpacing="2" fill="currentColor">
              {text}
            </text>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wm-pattern)" />
      </svg>
    </div>
  )
}

export default function Certificate() {
  const { uniqueId } = useParams()
  const { user } = useAuth()
  const [project, setProject] = useState(null)
  const [creator, setCreator] = useState(null)
  const [reviewer, setReviewer] = useState(null)
  const [reviewCount, setReviewCount] = useState(0)
  const [commits, setCommits] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [idCopied, setIdCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const certUrl = `${window.location.origin}/certificate/${uniqueId}`

  useEffect(() => { fetchData() }, [uniqueId])

  async function fetchData() {
    const { data: proj } = await supabase.from('projects').select('*').eq('unique_id', uniqueId).single()
    if (!proj) { setNotFound(true); setLoading(false); return }
    if (proj.visibility === 'private' && (!user || user.id !== proj.user_id)) { setNotFound(true); setLoading(false); return }

    const { data: prof } = await supabase.from('profiles')
      .select('full_name, display_name, username').eq('id', proj.user_id).single()

    let rev = null
    if (proj.verified_by_id) {
      const { data: revData } = await supabase.from('profiles')
        .select('full_name, display_name, reviewer_username, reviewer_title, reviewer_avatar_url')
        .eq('id', proj.verified_by_id).single()
      rev = revData
    }

    const { count } = await supabase
      .from('reviews').select('id', { count: 'exact', head: true })
      .eq('project_id', proj.id).eq('verdict', 'genuine')

    let evidence = []
    if (proj.show_evidence_on_cert) {
      const { data: commitData } = await supabase
        .from('commits').select('*').eq('project_id', proj.id)
        .order('created_at', { ascending: true })
      evidence = commitData || []
    }

    setProject(proj); setCreator(prof); setReviewer(rev); setReviewCount(count || 0); setCommits(evidence); setLoading(false)
  }

  async function handleDownloadCertificate() {
    if (!project) return
    setDownloading(true)
    try {
      const isVerified = project.status === 'verified'
      const accentColor = isVerified ? '#c8f55a' : '#888780'
      const darkBg = '#0f0f0e'
      const cardBg = '#1a1a18'
      const borderColor = isVerified ? 'rgba(200,245,90,0.35)' : 'rgba(136,135,128,0.3)'
      const textColor = '#f5f5f4'
      const text2Color = 'rgba(245,245,244,0.55)'
      const text3Color = 'rgba(245,245,244,0.35)'

      const W = 1400
      const H = 980
      const pad = 64
      const out = document.createElement('canvas')
      out.width = W
      out.height = H
      const ctx = out.getContext('2d')

      // Background
      ctx.fillStyle = darkBg
      ctx.fillRect(0, 0, W, H)

      // Card
      ctx.save()
      roundRect(ctx, pad / 2, pad / 2, W - pad, H - pad, 32)
      ctx.fillStyle = cardBg
      ctx.fill()
      ctx.strokeStyle = borderColor
      ctx.lineWidth = 1.5
      ctx.stroke()
      ctx.restore()

      // Top accent bar
      ctx.save()
      roundRect(ctx, pad / 2, pad / 2, W - pad, 7, { tl: 32, tr: 32, bl: 0, br: 0 })
      ctx.fillStyle = accentColor
      ctx.fill()
      ctx.restore()

      // Corner brackets
      const bS = 28, bT = 3, bP = pad / 2 + 24
      ctx.strokeStyle = isVerified ? 'rgba(200,245,90,0.45)' : 'rgba(136,135,128,0.3)'
      ctx.lineWidth = bT; ctx.lineCap = 'square'
      ctx.beginPath(); ctx.moveTo(bP, bP + bS); ctx.lineTo(bP, bP); ctx.lineTo(bP + bS, bP); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(W - bP - bS, bP); ctx.lineTo(W - bP, bP); ctx.lineTo(W - bP, bP + bS); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(bP, H - bP - bS); ctx.lineTo(bP, H - bP); ctx.lineTo(bP + bS, H - bP); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(W - bP - bS, H - bP); ctx.lineTo(W - bP, H - bP); ctx.lineTo(W - bP, H - bP - bS); ctx.stroke()

      // Logo mark
      const logoSize = 60, logoX = pad + 24, logoY = pad + 22
      ctx.save()
      roundRect(ctx, logoX, logoY, logoSize, logoSize, 14)
      ctx.fillStyle = accentColor
      ctx.fill()
      ctx.restore()
      ctx.fillStyle = '#0a0a0a'
      ctx.font = 'bold 34px Georgia, serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('H', logoX + logoSize / 2, logoY + logoSize / 2)

      // Site name & tagline
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'
      ctx.fillStyle = textColor
      ctx.font = 'bold 28px Georgia, serif'
      ctx.fillText('humanorigin.app', logoX + logoSize + 20, logoY + 28)
      ctx.fillStyle = text3Color
      ctx.font = '600 14px monospace'
      ctx.fillText('CERTIFICATE OF HUMAN ORIGIN', logoX + logoSize + 20, logoY + 52)

      // Status badge (top right)
      const badgeLabel = isVerified ? 'VERIFIED' : project.status.toUpperCase()
      const badgeColor = isVerified ? accentColor : '#888780'
      ctx.font = 'bold 14px monospace'
      const badgeW = ctx.measureText(badgeLabel).width + 36
      const badgeH = 36
      const badgeX = W - pad - 24 - badgeW
      const badgeY = logoY + (logoSize - badgeH) / 2
      ctx.save()
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 8)
      ctx.fillStyle = isVerified ? 'rgba(200,245,90,0.12)' : 'rgba(136,135,128,0.12)'
      ctx.fill()
      ctx.strokeStyle = badgeColor
      ctx.lineWidth = 1.5
      ctx.stroke()
      ctx.restore()
      ctx.fillStyle = badgeColor
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(badgeLabel, badgeX + badgeW / 2, badgeY + badgeH / 2)

      // Divider 1
      const div1Y = logoY + logoSize + 30
      ctx.strokeStyle = 'rgba(255,255,255,0.07)'
      ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(pad + 24, div1Y); ctx.lineTo(W - pad - 24, div1Y); ctx.stroke()

      // Statement section
      ctx.textAlign = 'center'
      ctx.fillStyle = text3Color
      ctx.font = '700 13px monospace'
      ctx.textBaseline = 'alphabetic'
      ctx.fillText('THIS CERTIFIES THAT', W / 2, div1Y + 48)

      ctx.fillStyle = textColor
      ctx.font = 'bold 58px Georgia, serif'
      ctx.textBaseline = 'alphabetic'
      const titleText = project.title.length > 44 ? project.title.slice(0, 42) + '…' : project.title
      ctx.fillText(titleText, W / 2, div1Y + 118)

      ctx.fillStyle = text2Color
      ctx.font = '20px Georgia, serif'
      ctx.fillText('was documented, reviewed, and recorded as a work of human origin.', W / 2, div1Y + 152)

      // Certificate ID bar
      const idBarY = div1Y + 182
      ctx.fillStyle = 'rgba(200,245,90,0.06)'
      ctx.fillRect(pad + 24, idBarY, W - (pad + 24) * 2, 48)
      ctx.strokeStyle = 'rgba(200,245,90,0.15)'
      ctx.lineWidth = 1
      ctx.strokeRect(pad + 24, idBarY, W - (pad + 24) * 2, 48)
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillStyle = text3Color; ctx.font = '700 13px monospace'
      ctx.fillText('CERTIFICATE ID', W / 2 - 150, idBarY + 24)
      ctx.fillStyle = accentColor; ctx.font = 'bold 18px monospace'
      ctx.fillText(project.unique_id, W / 2 + 60, idBarY + 24)

      // Fields section (left column) — large, legible text
      const fieldsY = idBarY + 72
      const fields = [
        ['CREATOR', creator?.display_name || creator?.full_name || 'Anonymous'],
        ['CATEGORY', project.custom_category ? `Other / ${project.custom_category}` : (project.category === 'Other (describe below)' ? 'Other' : project.category)],
        ['PROJECT STARTED', new Date(project.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
      ]
      if (isVerified && project.verified_at) {
        fields.push(['VERIFIED ON', new Date(project.verified_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })])
      }
      if (isVerified && reviewerName) {
        fields.push(['REVIEWED BY', reviewerName + (reviewer?.reviewer_title ? ` · ${reviewer.reviewer_title}` : '')])
      }

      let fY = fieldsY
      const fieldColW = W * 0.56
      fields.forEach(([label, value]) => {
        ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
        ctx.fillStyle = text3Color; ctx.font = '700 12px monospace'
        ctx.fillText(label, pad + 24, fY)
        ctx.fillStyle = textColor; ctx.font = '22px Georgia, serif'
        const valText = value.length > 50 ? value.slice(0, 48) + '…' : value
        ctx.fillText(valText, pad + 24, fY + 30)
        ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(pad + 24, fY + 46); ctx.lineTo(fieldColW, fY + 46); ctx.stroke()
        fY += 66
      })

      // QR code (right column) — shifted left and down vs before
      const drawQRAndFinish = () => {
        const qrCanvas = document.getElementById('qr-download-canvas')
        if (qrCanvas) {
          const qrSize = 230
          // shift left by extra 40px, down by extra 30px from fieldsY
          const qrX = W - pad - 24 - qrSize - 60
          const qrY2 = fieldsY + 30
          // QR box
          ctx.save()
          roundRect(ctx, qrX - 20, qrY2 - 20, qrSize + 40, qrSize + 40, 16)
          ctx.fillStyle = '#ffffff'
          ctx.fill()
          ctx.strokeStyle = 'rgba(200,245,90,0.5)'; ctx.lineWidth = 2; ctx.stroke()
          ctx.restore()
          ctx.drawImage(qrCanvas, qrX, qrY2, qrSize, qrSize)
          // "Scan to verify" label — clearly below QR with breathing room
          ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
          ctx.fillStyle = text3Color; ctx.font = '700 13px monospace'
          ctx.fillText('SCAN TO VERIFY', qrX + qrSize / 2, qrY2 + qrSize + 36)
          ctx.fillStyle = 'rgba(255,255,255,0.2)'; ctx.font = '14px monospace'
          ctx.fillText('humanorigin.app', qrX + qrSize / 2, qrY2 + qrSize + 58)
        }

        // Divider & footer
        const footerDivY = H - pad - 24 - 34
        ctx.strokeStyle = 'rgba(255,255,255,0.07)'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(pad + 24, footerDivY); ctx.lineTo(W - pad - 24, footerDivY); ctx.stroke()
        ctx.textBaseline = 'alphabetic'
        ctx.fillStyle = text3Color; ctx.font = '13px monospace'
        ctx.textAlign = 'left'
        ctx.fillText('Reviewed by a human — not an algorithm.', pad + 24, footerDivY + 24)
        ctx.textAlign = 'right'
        ctx.fillText(certUrl, W - pad - 24, footerDivY + 24)

        // Download
        const link = document.createElement('a')
        link.download = `HumanOrigin-Certificate-${project.unique_id}.png`
        link.href = out.toDataURL('image/png')
        link.click()
      }

      drawQRAndFinish()
    } catch (err) {
      console.error('Certificate download error:', err)
      alert('Download failed — please try again or use your browser\'s screenshot tool.')
    } finally {
      setDownloading(false)
    }
  }

  function handleDownloadQR() {
    const isVerified = project?.status === 'verified'
    const accentColor = isVerified ? '#c8f55a' : '#888780'
    const darkBg = '#0f0f0e'
    const cardBg = '#1a1a18'
    const borderColor = isVerified ? 'rgba(200,245,90,0.4)' : 'rgba(136,135,128,0.3)'

    // Get the QR code as an image from the hidden canvas
    const qrCanvas = document.getElementById('qr-download-canvas')
    if (!qrCanvas) return
    const qrDataUrl = qrCanvas.toDataURL('image/png')

    // Taller canvas to give every text element generous breathing room
    const W = 600
    const H = 900
    const out = document.createElement('canvas')
    out.width = W
    out.height = H
    const ctx = out.getContext('2d')

    // Background
    ctx.fillStyle = darkBg
    ctx.fillRect(0, 0, W, H)

    // Card body with rounded rect
    const pad = 30
    const cardW = W - pad * 2
    const cardH = H - pad * 2
    ctx.save()
    roundRect(ctx, pad, pad, cardW, cardH, 28)
    ctx.fillStyle = cardBg
    ctx.fill()
    ctx.strokeStyle = borderColor
    ctx.lineWidth = 1.5
    ctx.stroke()
    ctx.restore()

    // Top accent bar
    ctx.save()
    roundRect(ctx, pad, pad, cardW, 7, { tl: 28, tr: 28, bl: 0, br: 0 })
    ctx.fillStyle = accentColor
    ctx.fill()
    ctx.restore()

    // Corner brackets (decorative)
    const bSize = 22, bThick = 3, bPad = pad + 20
    ctx.strokeStyle = accentColor
    ctx.lineWidth = bThick
    ctx.lineCap = 'square'
    ctx.beginPath(); ctx.moveTo(bPad, bPad + bSize); ctx.lineTo(bPad, bPad); ctx.lineTo(bPad + bSize, bPad); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(W - bPad - bSize, bPad); ctx.lineTo(W - bPad, bPad); ctx.lineTo(W - bPad, bPad + bSize); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(bPad, H - bPad - bSize); ctx.lineTo(bPad, H - bPad); ctx.lineTo(bPad + bSize, H - bPad); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(W - bPad - bSize, H - bPad); ctx.lineTo(W - bPad, H - bPad); ctx.lineTo(W - bPad, H - bPad - bSize); ctx.stroke()

    // Logo mark "H" box
    const logoSize = 48, logoX = W / 2 - logoSize / 2, logoY = pad + 38
    ctx.save()
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 12)
    ctx.fillStyle = accentColor
    ctx.fill()
    ctx.restore()
    ctx.fillStyle = '#0a0a0a'
    ctx.font = 'bold 28px Georgia, serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('H', W / 2, logoY + logoSize / 2)

    // Site name — large and legible
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 26px Georgia, serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'alphabetic'
    ctx.fillText('humanorigin.app', W / 2, logoY + logoSize + 40)

    // Tagline — clearly readable
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = '700 16px monospace'
    ctx.fillText('CERTIFICATE OF HUMAN ORIGIN', W / 2, logoY + logoSize + 72)

    // Divider above QR
    ctx.strokeStyle = 'rgba(255,255,255,0.1)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(pad + 50, logoY + logoSize + 96)
    ctx.lineTo(W - pad - 50, logoY + logoSize + 96)
    ctx.stroke()

    // QR image — large and centered
    const qrSize = 300
    const qrX = (W - qrSize) / 2
    const qrY = logoY + logoSize + 120

    // QR container box
    ctx.save()
    roundRect(ctx, qrX - 22, qrY - 22, qrSize + 44, qrSize + 44, 16)
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    ctx.strokeStyle = 'rgba(200,245,90,0.5)'
    ctx.lineWidth = 2
    ctx.stroke()
    ctx.restore()

    const qrImg = new Image()
    qrImg.onload = () => {
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)

      // Divider below QR
      const divY = qrY + qrSize + 48
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(pad + 50, divY)
      ctx.lineTo(W - pad - 50, divY)
      ctx.stroke()

      // ── SCAN TO VERIFY ── large, prominent
      ctx.fillStyle = 'rgba(255,255,255,0.75)'
      ctx.font = '700 22px monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'alphabetic'
      ctx.fillText('SCAN TO VERIFY', W / 2, divY + 46)

      // Thin rule between sections
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(pad + 70, divY + 66)
      ctx.lineTo(W - pad - 70, divY + 66)
      ctx.stroke()

      // ── CERTIFICATE ID label ──
      ctx.fillStyle = 'rgba(255,255,255,0.45)'
      ctx.font = '700 16px monospace'
      ctx.textAlign = 'center'
      ctx.fillText('CERTIFICATE ID', W / 2, divY + 102)

      // ── Actual certificate ID value ── large accent colour
      ctx.fillStyle = accentColor
      ctx.font = 'bold 22px monospace'
      ctx.fillText(project.unique_id, W / 2, divY + 136)

      // Thin rule
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(pad + 70, divY + 158)
      ctx.lineTo(W - pad - 70, divY + 158)
      ctx.stroke()

      // Bottom watermark — tastefully small but still legible
      ctx.fillStyle = 'rgba(255,255,255,0.2)'
      ctx.font = '13px monospace'
      ctx.textAlign = 'center'
      ctx.fillText('Verified by a human — not an algorithm', W / 2, H - pad - 18)

      // Download
      const link = document.createElement('a')
      link.download = `HumanOrigin-QR-${project.unique_id}.png`
      link.href = out.toDataURL('image/png')
      link.click()
    }
    qrImg.src = qrDataUrl
  }

  // Canvas rounded rect helper (works cross-browser)
  function roundRect(ctx, x, y, w, h, r) {
    if (typeof r === 'number') r = { tl: r, tr: r, bl: r, br: r }
    ctx.beginPath()
    ctx.moveTo(x + r.tl, y)
    ctx.lineTo(x + w - r.tr, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r.tr)
    ctx.lineTo(x + w, y + h - r.br); ctx.quadraticCurveTo(x + w, y + h, x + w - r.br, y + h)
    ctx.lineTo(x + r.bl, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r.bl)
    ctx.lineTo(x, y + r.tl); ctx.quadraticCurveTo(x, y, x + r.tl, y)
    ctx.closePath()
  }

  async function handleShare() {
    if (navigator.share) await navigator.share({ title: `HumanOrigin Certificate — ${project?.title}`, url: certUrl })
    else { await navigator.clipboard.writeText(certUrl); alert('Link copied!') }
  }

  async function handleCopyId() {
    await navigator.clipboard.writeText(project.unique_id)
    setIdCopied(true)
    setTimeout(() => setIdCopied(false), 2000)
  }

  async function handleCopyUrl() {
    await navigator.clipboard.writeText(certUrl)
    setUrlCopied(true)
    setTimeout(() => setUrlCopied(false), 2000)
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  if (notFound) return (
    <div className="container" style={{ textAlign: 'center', padding: 80 }}>
      <h2>Certificate not found</h2>
      <p style={{ color: 'var(--text-2)', marginTop: 8 }}>This certificate is private or doesn't exist.</p>
      <Link to="/browse" className="btn btn-secondary" style={{ marginTop: 24 }}>Browse verified work</Link>
    </div>
  )

  const creatorName = creator?.display_name || creator?.full_name || 'Anonymous'
  const reviewerName = reviewer?.display_name || reviewer?.full_name || project?.verified_by_name || null
  const isVerified = project.status === 'verified'
  const isOwner = user && user.id === project.user_id
  const showEvidence = project.show_evidence_on_cert && commits.length > 0
  const categoryUrl = `/browse?category=${encodeURIComponent(project.category)}`
  const projectUrl = `/project/${project.id}`

  return (
    <div className="cert-page page-fade">
      <div className="container">

        {/* Hidden high-res QR canvas for download */}
        <div style={{ position: 'fixed', left: -9999, top: -9999, pointerEvents: 'none', opacity: 0 }}>
          <QRCodeCanvas
            id="qr-download-canvas"
            value={certUrl}
            size={520}
            bgColor="#ffffff"
            fgColor="#0f0f0e"
            level="M"
          />
        </div>

        {/* Actions bar */}
        <div className="cert-actions-bar">
          <Link to={creator?.username ? `/u/${creator.username}` : '/browse'} className="btn btn-ghost btn-sm">
            ← {creator?.username ? `@${creator.username}` : 'Browse'}
          </Link>
          <div className="cert-action-btns">
            <Link to={projectUrl} className="btn btn-ghost btn-sm">View project →</Link>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleDownloadCertificate}
              disabled={downloading}
            >
              {downloading ? 'Preparing…' : 'Download Certificate'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleDownloadQR}>Download QR</button>
            <button className="btn btn-secondary btn-sm" onClick={handleShare}>Share →</button>
          </div>
        </div>

        <div className="cert-wrapper">
          <div className={`cert-document ${isVerified ? 'cert-verified' : ''}`}>

            <DiagonalWatermark text={project.unique_id} />

            <div className="cert-top-bar" />
            <div className="cert-corner cert-corner-tl" />
            <div className="cert-corner cert-corner-tr" />
            <div className="cert-corner cert-corner-bl" />
            <div className="cert-corner cert-corner-br" />

            {/* Header */}
            <div className="cert-header">
              <div className="cert-logo-row">
                <div className="cert-logo-mark">H</div>
                <div>
                  <div className="cert-site-name">humanorigin.app</div>
                  <div className="cert-site-tagline">Certificate of Human Origin</div>
                </div>
              </div>
              <StatusBadge status={project.status} large />
            </div>

            <div className="cert-divider" />

            {/* Statement */}
            <div className="cert-statement">
              <span className="cert-kicker">This certifies that</span>
              <strong>{project.title}</strong>
              <span>was documented, reviewed, and recorded as a work of human origin.</span>
            </div>

            {/* Certificate ID banner */}
            <div className="cert-id-banner">
              <span className="cert-id-label">Certificate ID</span>
              <span className="cert-id-value">{project.unique_id}</span>
              <button className="cert-id-copy" onClick={handleCopyId}>
                {idCopied ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            {/* Body */}
            <div className="cert-body">
              <div className="cert-main-info">
                <div className="cert-field">
                  <span className="cert-field-label">Title of work</span>
                  <span className="cert-field-value cert-title">{project.title}</span>
                </div>
                <div className="cert-field">
                  <span className="cert-field-label">Creator</span>
                  <span className="cert-field-value">
                    {creator?.username
                      ? <Link to={`/u/${creator.username}`} className="cert-link">{creatorName}</Link>
                      : creatorName}
                  </span>
                </div>
                <div className="cert-field">
                  <span className="cert-field-label">Category</span>
                  <span className="cert-field-value">
                    <Link to={categoryUrl} className="cert-link">
                      {project.custom_category ? `Other / ${project.custom_category}` : (project.category === 'Other (describe below)' ? 'Other' : project.category)}
                    </Link>
                  </span>
                </div>
                <div className="cert-field">
                  <span className="cert-field-label">Project started</span>
                  <span className="cert-field-value">
                    {new Date(project.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                {isVerified && project.verified_at && (
                  <div className="cert-field">
                    <span className="cert-field-label">Verified on</span>
                    <span className="cert-field-value">
                      {new Date(project.verified_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                )}
                {isVerified && reviewerName && (
                  <div className="cert-field">
                    <span className="cert-field-label">Reviewed by</span>
                    <span className="cert-field-value cert-verifier">
                      {reviewer?.reviewer_avatar_url && (
                        <img src={reviewer.reviewer_avatar_url} alt={reviewerName} className="cert-reviewer-avatar" />
                      )}
                      {reviewer?.reviewer_username
                        ? <Link to={`/reviewer/${reviewer.reviewer_username}`} className="cert-link">{reviewerName}</Link>
                        : reviewerName}
                      {reviewer?.reviewer_title && <span className="cert-reviewer-title"> · {reviewer.reviewer_title}</span>}
                    </span>
                  </div>
                )}
                {isVerified && !reviewerName && reviewCount > 0 && (
                  <div className="cert-field">
                    <span className="cert-field-label">Verified by</span>
                    <span className="cert-field-value cert-verifier">
                      {reviewCount} independent community reviewer{reviewCount === 1 ? '' : 's'}
                    </span>
                  </div>
                )}

                {/* Certificate URL — click to copy */}
                <div className="cert-field cert-field-url">
                  <span className="cert-field-label">Certificate URL</span>
                  <button
                    className="cert-url-copy-btn"
                    onClick={handleCopyUrl}
                    title="Click to copy certificate URL"
                  >
                    <span className="cert-url-text">{certUrl}</span>
                    <span className="cert-url-copy-badge">
                      {urlCopied ? '✓ Copied' : 'Copy'}
                    </span>
                  </button>
                </div>
              </div>

              {/* QR column */}
              <div className="cert-qr-col">
                <div className="cert-qr-box">
                  <QRCodeSVG
                    value={certUrl}
                    size={210}
                    bgColor="#ffffff"
                    fgColor="#0f0f0e"
                    level="M"
                  />
                </div>
                <div className="cert-qr-label">Scan to verify</div>
                <div className="cert-qr-hint">humanorigin.app</div>
                <div className="cert-qr-url">{project.unique_id}</div>
              </div>
            </div>

            {/* Description */}
            {project.description && (
              <div className="cert-description">
                <span className="cert-field-label">About this work</span>
                <p>{project.description}</p>
              </div>
            )}

            {/* Evidence callout */}
            {showEvidence && (
              <div className="cert-evidence-callout">
                <div className="cert-evidence-callout-icon">📋</div>
                <div className="cert-evidence-callout-text">
                  <strong>Process evidence available</strong>
                  <span>This certificate includes a timestamped project timeline used during review.</span>
                </div>
                <a href="#evidence-timeline" className="cert-evidence-callout-link">View timeline ↓</a>
              </div>
            )}

            <div className="cert-divider" />

            {/* Footer */}
            <div className="cert-footer">
              <span>Reviewed by a human — not an algorithm. humanorigin.app</span>
              <span>{certUrl}</span>
            </div>
          </div>
        </div>

        {isOwner && isVerified && (
          <div className="card" style={{ marginTop: 28, padding: '24px 28px' }}>
            <EmbedBadge uniqueId={uniqueId} />
          </div>
        )}

        {isVerified && (
          <div className="reviewer-trust" style={{ marginTop: 14 }}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.5" stroke="#c8f55a" strokeWidth="1.2"/>
              <path d="M4.5 7.5l2 2 4-4" stroke="#c8f55a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            This certificate was reviewed and approved by a human member of the HumanOrigin team — not an automated system.
          </div>
        )}

        {showEvidence && (
          <section id="evidence-timeline" className="cert-evidence-section">
            <div className="cert-evidence-header">
              <div>
                <span className="cert-field-label">Evidence reviewed</span>
                <h2>Project timeline</h2>
              </div>
              <Link to={projectUrl} className="btn btn-secondary btn-sm">View full project →</Link>
            </div>
            <p className="cert-evidence-intro">
              This timeline shows the process evidence the creator chose to make visible with this certificate.
            </p>
            <div className="timeline cert-evidence-timeline">
              {commits.map(c => (
                <div key={c.id} className="timeline-item">
                  <div className="timeline-dot" />
                  <div className="timeline-meta">{new Date(c.created_at).toLocaleString()} · {c.commit_type}</div>
                  <div className="timeline-msg">{c.message}</div>
                  {c.commit_type === 'photo' && c.media_url && (
                    <img src={c.media_url} alt="commit" className="timeline-photo" />
                  )}
                  {c.commit_type === 'link' && c.media_url && (
                    <a href={c.media_url} target="_blank" rel="noopener noreferrer" className="timeline-link">🔗 {c.media_url}</a>
                  )}
                  {c.commit_type === 'video' && c.media_url && <VideoEmbed url={c.media_url} />}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
